/**
 * Browser-mode interrupted-session persistence (B.2).
 *
 * Browser STT (Web Speech API) doesn't expose raw audio — only an
 * interim text stream via `recognition.onresult`. When the user is
 * recording in browser mode and the tab hides, there's no Blob to
 * persist; the best we can do is stash the recovered text to localStorage
 * so the resume card can show "Keep typing?" with the captured words inline.
 *
 * The localStorage key remains `adia-interrupted-interim` with its historical
 * single-object shape. A new interruption refuses to overwrite an unresolved
 * existing entry; recovery is a one-way door even though this legacy slot can
 * represent only one browser session.
 *
 * CORE-CONTENT CUTOVER. This key is in the preservation set: it can hold
 * the only copy of a prayer that was being spoken when the tab hid, and
 * it survives reloads, so it is exactly the kind of content the cutover
 * exists to keep. Both mutating helpers below therefore carry the shared
 * read-only guard at the persistence boundary, the same posture db.ts
 * takes: hiding the surface that calls them is presentation, this guard
 * is the invariant. Reading stays open so the resume card and the escrow
 * exporter can still see the value.
 */

// RELATIVE import (db.ts precedent): keeps this leaf loadable by
// scripts/probe-core-readonly.mjs under plain tsx, which does not map the
// '@/' alias, so the guards below are covered at RUNTIME rather than by a
// source-text assertion.
import { isCoreReadOnly } from './core-cutover';
import { parseScriptureAnchor } from './scripture-anchor';
import type { ScriptureAnchor } from '../types/prayer';

const KEY = 'adia-interrupted-interim';

export interface BrowserInterruptedInterim {
  text: string;
  capturedAt: string;
  /** Present only for an interrupted Pray-on-Scripture capture. */
  scriptureAnchor?: ScriptureAnchor;
}

export function parseBrowserInterruptedInterim(
  raw: string | null,
): BrowserInterruptedInterim | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<BrowserInterruptedInterim>;
    if (typeof parsed.text !== 'string' || !parsed.text.trim()) return null;
    if (typeof parsed.capturedAt !== 'string') return null;
    // Legacy values have no scriptureAnchor and remain valid. If an optional
    // persisted anchor is malformed, retain the user's words but drop only the
    // untrusted provenance; recovery must never attach text to a false passage.
    const scriptureAnchor = Object.prototype.hasOwnProperty.call(
      parsed,
      'scriptureAnchor',
    )
      ? parseScriptureAnchor(parsed.scriptureAnchor)
      : null;
    return {
      text: parsed.text,
      capturedAt: parsed.capturedAt,
      ...(scriptureAnchor ? { scriptureAnchor } : {}),
    };
  } catch {
    return null;
  }
}

export function saveBrowserInterruptedInterim(
  text: string,
  scriptureAnchor?: ScriptureAnchor,
): BrowserInterruptedInterim | null {
  if (isCoreReadOnly()) return null;
  const normalized = text.trim();
  if (!normalized) return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw !== null) {
      // Known or malformed, an occupied preservation slot is never silently
      // overwritten. The caller treats null as a persistence failure and
      // keeps the active owner/runtime fenced.
      return null;
    }
    // Capture a validated detached snapshot. Invalid optional provenance is
    // omitted while the words are still persisted as an ordinary recovery.
    const validatedAnchor = scriptureAnchor
      ? parseScriptureAnchor(scriptureAnchor)
      : null;
    const entry: BrowserInterruptedInterim = {
      text: normalized,
      capturedAt: new Date().toISOString(),
      ...(validatedAnchor ? { scriptureAnchor: validatedAnchor } : {}),
    };
    localStorage.setItem(KEY, JSON.stringify(entry));
    return entry;
  } catch {
    return null;
  }
}

export function loadBrowserInterruptedInterim(): BrowserInterruptedInterim | null {
  try {
    return parseBrowserInterruptedInterim(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

export function clearBrowserInterruptedInterim(): boolean {
  if (isCoreReadOnly()) return false;
  try {
    localStorage.removeItem(KEY);
    return true;
  } catch {
    return false;
  }
}
